/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button, Icon, Stack, Flex, SearchInput, Box } from '@tonic-one/react';
import { AddIcon, ExportIcon, ImportIcon } from '@tonic-one/react-icons';
import BottomPagination from '@Report/BottomPagination';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { DlpKeyword, DpBuiltinEnum, FilterParams } from '@Policy/utils/types';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { DlpKeywordListModalNameEnum, DlpKeywordListModalState } from '../type';
import { useTranslation } from 'react-i18next';
import KeywordTable from './Table';
import { PolicyApi } from '@Apis';
import { KeywordModal } from './Modal';
import { exportFile } from '../../../../../utils/common';
import CSpinner from '@Components/CSpinner';
import i18next from 'i18next';
import { FileType } from '../../../../../types/globalSetting';
import ImportModal from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/ImportModal';

const KeywordLists = () => {
  const { t } = useTranslation(['common', 'policy']);
  const [filterParams, setFilterParams] = useState<FilterParams>({
    keyword: '',
    offset: 1,
    size: 50,
  });
  const setFilterParamsWithDebounce = debounce(setFilterParams, 200);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<DlpKeyword[]>([]);
  const [data, setData] = useState<DlpKeyword[]>([]);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [modalState, setModalState] = useState<DlpKeywordListModalState>({
    name: DlpKeywordListModalNameEnum.None,
    data: null,
  });
  const { widthEditablePermission } = usePermissionStore();
  const dlpEditPermission = widthEditablePermission(
    PermissionNameEnum.dlpPolicy
  );
  const onImport = async (content: FormData) => {
    try {
      setLoading(true);
      await PolicyApi.importDlpEntity(content);
      setIsImportModalVisible(false);
      setRefreshTableFlag(new Date());
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  const [refreshTableFlag, setRefreshTableFlag] = useState(new Date());
  const refreshTable = useCallback(() => {
    setRefreshTableFlag(new Date());
  }, []);
  const onCloseModal = useCallback(() => {
    setModalState({
      name: DlpKeywordListModalNameEnum.None,
      data: null,
    });
  }, []);
  const onClickDownload = async () => {
    for (const row of selectedRows) {
      if (row.dpKeywordBuiltin === DpBuiltinEnum.Predefined) {
        window.showNotification({
          titleMsg: t('policy.dataIdentifiers.keywordLists.exportWarning'),
          severity: 'warning',
        });
        return;
      }
    }

    try {
      setLoading(true);
      const res = await PolicyApi.downloadDlpKeyword(
        selectedRows.map((v) => v.cfgIndex)
      );
      exportFile(
        'keywords_export.xml',
        res.data as any,
        'application/octet-stream'
      );
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  // const cellHeight = '36px';
  useEffect(() => {
    PolicyApi.getDlpKeywords(i18next.language).then((res) => {
      setData(res.data);
    });
  }, [refreshTableFlag]);
  return (
    <Box height="100%">
      {loading && <CSpinner />}
      <ImportModal
        isVisible={isImportModalVisible}
        onClose={() => {
          setIsImportModalVisible(false);
        }}
        onImport={onImport}
        acceptFileType={FileType.XML}
      />
      <Stack height="100%">
        <Flex
          justifyContent="space-between"
          height="52px"
          p="10px 16px 10px 16px"
        >
          <Flex height="32px" gap="8px">
            <Button
              variant="primary"
              onClick={() => {
                setModalState({
                  name: DlpKeywordListModalNameEnum.Add,
                  data: null,
                });
                console.log('add');
              }}
              disabled={!dlpEditPermission}
            >
              <Icon as={AddIcon} mr="2x" size="16px" />
              {t('common.action.label.add')}
            </Button>
            <SearchInput
              width="244px"
              placeholder={t('common.label.nameAndDesc')}
              onChange={(e: any) => {
                setFilterParamsWithDebounce({
                  keyword: e.target.value,
                  offset: 1,
                  size: 50,
                });
              }}
              onClearInput={() => {
                setFilterParamsWithDebounce({
                  keyword: '',
                  offset: 1,
                  size: 50,
                });
              }}
            />
          </Flex>
          <Flex height="32px" gap="8px">
            <Button
              variant="secondary"
              disabled={!dlpEditPermission}
              onClick={() => {
                setIsImportModalVisible(true);
              }}
            >
              <Icon as={ImportIcon} mr="2x" size="16px" />
              {t('common.action.label.import')}
            </Button>
            <Button variant="secondary" onClick={onClickDownload}>
              <Icon as={ExportIcon} mr="2x" size="16px" />
              {t('common.action.label.export')}
            </Button>
          </Flex>
        </Flex>
        <KeywordTable
          setModalState={setModalState}
          setSelectedRows={setSelectedRows}
          filterParams={filterParams}
          data={data}
          refreshTable={refreshTable}
        />
        <BottomPagination
          totalCount={data.length}
          currentPage={filterParams.offset}
          pageSize={filterParams.size}
          changePageSize={(size: number) => {
            setFilterParamsWithDebounce({
              ...filterParams,
              size,
              offset: 1,
            });
          }}
          changeCurrentPage={(offset: number) => {
            setFilterParamsWithDebounce({
              ...filterParams,
              offset,
            });
          }}
        />
        <KeywordModal
          modalState={modalState}
          onClose={onCloseModal}
          refreshTable={refreshTable}
        />
      </Stack>
    </Box>
  );
};

export default KeywordLists;
